import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ProjectModel } from '../project/project.model';
import { ProjectService } from '../project/project.service';
import { UsersService } from '../users/users.service';
import { TimesheetGetDTO, WorkLogGetDetailDTO } from './timesheet.model';
import { TimesheetsService } from './timesheets.service';
import { UserService } from '../../core/services/user.service';
import { RoleType } from '../../core/services/role.service';

@Injectable({ providedIn: 'root' })
export class TimesheetResolver
  implements
    Resolve<
      | TimesheetGetDTO[]
      | null
      | boolean
      | { projects: ProjectModel[] }
      | {
          projects: ProjectModel[] | null;
          worklog: WorkLogGetDetailDTO | null;
        }
      | {
          clients: {
            clientId: string;
            firstName: string;
            lastName: string;
            rate: string;
          }[];
          timesheets: TimesheetGetDTO[];
        }
    >
{
  constructor(private svc: TimesheetsService) {}
  projectsSvc = inject(ProjectService);
  userSvc = inject(UserService);
  usersSvc = inject(UsersService);

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<
    | TimesheetGetDTO[]
    | null
    | boolean
    | { projects: ProjectModel[] }
    | {
        projects: ProjectModel[] | null;
        worklog: WorkLogGetDetailDTO | null;
      }
    | {
        clients: {
          clientId: string;
          firstName: string;
          lastName: string;
          rate: string;
        }[];
        timesheets: TimesheetGetDTO[];
      }
  > {
    const clientId = route.queryParamMap.get('clientId');

    if (route.url.some((segment) => segment.path === 'add')) {
      return forkJoin({
        projects: this.projectsSvc
          .getClientProjects(clientId!)
          .pipe(map((p) => p.data)),
      });
    }

    if (route.url.some((segment) => segment.path === 'edit')) {
      return forkJoin({
        projects: this.projectsSvc
          .getClientProjects(clientId!)
          .pipe(map((p) => p.data)),
        worklog: this.svc.getTimeSheetById(route.paramMap.get('worklogId')),
      });
    }

    // accept optional query params: userId, employeeId, startDate, endDate, clientId
    const employeeId = route.queryParamMap.get('employeeId') || undefined;
    const employeeUserId =
      route.queryParamMap.get('employeeUserId') || undefined;
    const startDateParam = route.queryParamMap.get('startDate') || undefined;
    const endDateParam = route.queryParamMap.get('endDate') || undefined;
    const clientUserId = route.queryParamMap.get('clientUserId') || undefined;

    const startOfWeek = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      d.setDate(d.getDate() - day); // Sunday
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const addDays = (date: Date, days: number) => {
      const d = new Date(date);
      d.setDate(d.getDate() + days);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const fmt = (d: Date) => d.toISOString().split('T')[0]; // YYYY-MM-DD
    const now = new Date();
    const computedStart = startOfWeek(now);
    const computedEnd = addDays(computedStart, 6);
    const startDate = startDateParam || fmt(computedStart);

    // Calculate endDate based on startDateParam if provided, otherwise use computed end date
    let endDate: string;
    if (startDateParam) {
      const [year, month, day] = startDateParam.split('-').map(Number);
      const startDateObj = new Date(year, month - 1, day);
      const endDateObj = addDays(startDateObj, 6);
      endDate = fmt(endDateObj);
    } else {
      endDate = fmt(computedEnd);
    }

    // Case 1: Admin
    return this.userSvc.user$.pipe(
      switchMap((currentUser) => {
        if (currentUser!.role.name === RoleType.Admin) {
          return this.usersSvc.getUserById(employeeUserId!).pipe(
            switchMap((user) => {
              return forkJoin({
                employeeName: of(user.firstName + ' ' + user.lastName),
                clients: of(user.employee!.clientsRelationship),
                timesheets: this.svc.getTimeSheet(
                  startDateParam || startDate,
                  endDateParam || endDate,
                  employeeId,
                  clientId || user.employee?.clientsRelationship[0]?.clientId,
                ),
              });
            }),
          );
        } else if (currentUser!.role.name === RoleType.Client) {
          return this.usersSvc.getUserById(clientUserId!).pipe(
            switchMap((client) => {
              const employee = client.client.employeesRelationship?.find(
                (empRel) => empRel.employeeId === employeeId,
              );
              return forkJoin({
                employeeName: of(
                  employee?.firstName + ' ' + employee?.lastName,
                ),
                clients: of([
                  {
                    clientId: client.client.id,
                    firstName: client.firstName,
                    lastName: client.lastName,
                    rate: '',
                  },
                ]),
                timesheets: this.svc.getTimeSheet(
                  startDateParam || startDate,
                  endDateParam || endDate,
                  employeeId,
                  client.client.id,
                ),
              });
            }),
          );
        } else {
          // is Employee
          return this.usersSvc.getUserById(currentUser!.userId).pipe(
            switchMap((user) => {
              return forkJoin({
                employeeName: of(user.firstName + ' ' + user.lastName),
                clients: of(user.employee!.clientsRelationship),
                timesheets: this.svc.getTimeSheet(
                  startDateParam || startDate,
                  endDateParam || endDate,
                  undefined,
                  clientId || user.employee?.clientsRelationship[0]?.clientId,
                ),
              });
            }),
          );
        }
      }),
    );
  }
}
